import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Copy, KeyRound, Pencil, Plus, Search, ShieldCheck, Trash2, UserCheck, UserPlus, Users, UserX, X } from "lucide-react";
import Layout from "../../layout/Layout";
import {
  apiHata,
  ekleDepartman,
  ekleKullanici,
  getPersonelContext,
  getPersonelListe,
  guncelleDepartman,
  guncelleKullanici,
  kullaniciDurum,
  kullaniciSifreSifirla,
} from "../../../api/admin/personelYonetimiApi";
import "./PersonelYonetimi.css";

/**
 * Personel (panel kullanıcısı) yönetimi: ekleme, departman / rol atama, pasife alma, geçici şifre.
 * Yetkiler: personel_view (liste), personel_manage (işlemler), personel_departman_manage (departmanlar).
 */

const bosForm = () => ({ name: "", surname: "", email: "", phone: "", departmanId: "", subDepartmanId: "", roleId: "", otpStatus: true, sifre: "" });
const tarih = (v) => (v ? new Date(v).toLocaleDateString("tr-TR") : "—");

export default function PersonelYonetimiPage() {
  const [ctx, setCtx] = useState(null);
  const [sekme, setSekme] = useState("personel");
  const [liste, setListe] = useState({ personel: [], sayilar: { aktif: 0, pasif: 0 }, pagination: null });
  const [filtre, setFiltre] = useState({ search: "", departmanId: "", roleId: "", durum: "aktif", page: 1 });
  const [arama, setArama] = useState("");
  const [yukleniyor, setYukleniyor] = useState(false);
  const [bildirim, setBildirim] = useState(null);
  const [form, setForm] = useState(null); // { mod: "ekle" | "duzenle", id?, ...alanlar }
  const [sifre, setSifre] = useState(null); // { kisi, sifre }

  const yetki = ctx?.yetkiler || {};
  const departmanMap = useMemo(() => new Map((ctx?.departmanlar || []).map((d) => [String(d._id), d])), [ctx]);

  const contextYukle = useCallback(async () => {
    try {
      const r = await getPersonelContext();
      setCtx(r.data.data);
    } catch (e) {
      setBildirim({ tip: "hata", mesaj: apiHata(e) });
    }
  }, []);

  const listeYukle = useCallback(async (f) => {
    setYukleniyor(true);
    try {
      const r = await getPersonelListe({ ...f, limit: 50 });
      setListe(r.data.data);
    } catch (e) {
      setBildirim({ tip: "hata", mesaj: apiHata(e) });
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    contextYukle();
  }, [contextYukle]);
  useEffect(() => {
    listeYukle(filtre);
  }, [filtre, listeYukle]);

  // aramayı yazarken her tuşta istek atmamak için kısa gecikme
  useEffect(() => {
    const t = setTimeout(() => setFiltre((f) => (f.search === arama ? f : { ...f, search: arama, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [arama]);

  const kaydet = async (e) => {
    e.preventDefault();
    const { mod, id, ...alanlar } = form;
    try {
      if (mod === "ekle") {
        const r = await ekleKullanici(alanlar);
        const { personel, geciciSifre } = r.data.data;
        if (geciciSifre) setSifre({ kisi: `${personel.name} ${personel.surname}`, email: personel.email, sifre: geciciSifre });
        setBildirim({ tip: "ok", mesaj: `${personel.name} ${personel.surname} eklendi.` });
      } else {
        const { sifre: _s, ...guncel } = alanlar;
        await guncelleKullanici({ id, ...guncel });
        setBildirim({ tip: "ok", mesaj: "Personel güncellendi." });
      }
      setForm(null);
      listeYukle(filtre);
    } catch (err) {
      setBildirim({ tip: "hata", mesaj: apiHata(err) });
    }
  };

  const durumDegistir = async (p) => {
    const aktif = !p.isActive;
    if (!aktif && !window.confirm(`${p.name} ${p.surname} pasife alınsın mı? Hemen oturumu kapanır ve giriş yapamaz.`)) return;
    try {
      await kullaniciDurum(p._id, aktif);
      setBildirim({ tip: "ok", mesaj: aktif ? `${p.name} ${p.surname} aktif edildi.` : `${p.name} ${p.surname} pasife alındı.` });
      listeYukle(filtre);
    } catch (e) {
      setBildirim({ tip: "hata", mesaj: apiHata(e) });
    }
  };

  const sifreSifirla = async (p) => {
    if (!window.confirm(`${p.name} ${p.surname} için yeni geçici şifre oluşturulsun mu? Eski şifresi geçersiz olur.`)) return;
    try {
      const r = await kullaniciSifreSifirla(p._id);
      setSifre({ kisi: `${p.name} ${p.surname}`, email: p.email, sifre: r.data.data.geciciSifre });
    } catch (e) {
      setBildirim({ tip: "hata", mesaj: apiHata(e) });
    }
  };

  const duzenleAc = (p) =>
    setForm({
      mod: "duzenle",
      id: p._id,
      name: p.name,
      surname: p.surname,
      email: p.email,
      phone: p.phone,
      departmanId: String(p.departmanId || ""),
      subDepartmanId: String(p.subDepartmanId || ""),
      roleId: String(p.roleId || ""),
      otpStatus: p.otpStatus,
      sifre: "",
    });

  return (
    <Layout>
      <div className="pyn-page">
        <section className="pyn-hero">
          <div>
            <div className="pyn-kicker">
              <ShieldCheck size={15} /> Admin
            </div>
            <h1>Personel Yönetimi</h1>
            <p>Panele giriş yapan personeli ekleyin, departman ve rol atayın, hesapları pasife alın ya da geçici şifre oluşturun.</p>
          </div>
          <div className="pyn-hero-card">
            <span>Aktif personel</span>
            <strong>{liste.sayilar.aktif}</strong>
            <small>{liste.sayilar.pasif} pasif hesap</small>
          </div>
        </section>

        {bildirim && (
          <div className={`pyn-bildirim ${bildirim.tip}`} role="status">
            <span>{bildirim.mesaj}</span>
            <button type="button" aria-label="Kapat" onClick={() => setBildirim(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        <div className="pyn-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={sekme === "personel"} onClick={() => setSekme("personel")}>
            <Users size={16} /> Personel
          </button>
          <button type="button" role="tab" aria-selected={sekme === "departman"} onClick={() => setSekme("departman")}>
            <Building2 size={16} /> Departmanlar
          </button>
        </div>

        {sekme === "personel" ? (
          <section className="pyn-panel">
            <div className="pyn-toolbar">
              <label className="pyn-search">
                <Search size={16} />
                <input value={arama} onChange={(e) => setArama(e.target.value)} placeholder="Ad, soyad, e-posta, telefon" aria-label="Personel ara" />
              </label>
              <select aria-label="Departman" value={filtre.departmanId} onChange={(e) => setFiltre({ ...filtre, departmanId: e.target.value, page: 1 })}>
                <option value="">Tüm departmanlar</option>
                {(ctx?.departmanlar || []).map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                    {d.isActive ? "" : " (pasif)"}
                  </option>
                ))}
              </select>
              <select aria-label="Rol" value={filtre.roleId} onChange={(e) => setFiltre({ ...filtre, roleId: e.target.value, page: 1 })}>
                <option value="">Tüm roller</option>
                {(ctx?.roller || []).map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <select aria-label="Durum" value={filtre.durum} onChange={(e) => setFiltre({ ...filtre, durum: e.target.value, page: 1 })}>
                <option value="aktif">Aktif</option>
                <option value="pasif">Pasif</option>
                <option value="hepsi">Hepsi</option>
              </select>
              {yetki.yonet && (
                <button type="button" className="pyn-btn primary" onClick={() => setForm({ mod: "ekle", ...bosForm() })}>
                  <UserPlus size={16} /> Personel ekle
                </button>
              )}
            </div>

            <div className="pyn-table-wrap">
              <table className="pyn-table">
                <thead>
                  <tr>
                    <th>Ad soyad</th>
                    <th>E-posta</th>
                    <th>Telefon</th>
                    <th>Departman</th>
                    <th>Rol</th>
                    <th>Giriş</th>
                    <th>Kayıt</th>
                    {yetki.yonet && <th className="pyn-actions-h">İşlemler</th>}
                  </tr>
                </thead>
                <tbody>
                  {liste.personel.map((p) => {
                    const ben = String(p._id) === String(ctx?.benId);
                    return (
                      <tr key={p._id} className={p.isActive ? "" : "pasif"}>
                        <td>
                          <b>
                            {p.name} {p.surname}
                          </b>
                          {ben && <span className="pyn-chip">siz</span>}
                          {!p.isActive && <span className="pyn-chip off">pasif</span>}
                        </td>
                        <td className="pyn-mono">{p.email}</td>
                        <td>{p.phone || "—"}</td>
                        <td>
                          {p.departmanAd || "—"}
                          {p.altDepartmanAd && <small>{p.altDepartmanAd}</small>}
                        </td>
                        <td>{p.rolAd || "—"}</td>
                        <td>{p.otpStatus ? "Şifre + doğrulama kodu" : "Yalnız şifre"}</td>
                        <td>{tarih(p.createdAt)}</td>
                        {yetki.yonet && (
                          <td>
                            <div className="pyn-row-actions">
                              <button type="button" className="pyn-icon" title="Düzenle" aria-label="Düzenle" onClick={() => duzenleAc(p)}>
                                <Pencil size={15} />
                              </button>
                              {!ben && (
                                <>
                                  <button type="button" className="pyn-icon" title="Geçici şifre oluştur" aria-label="Geçici şifre oluştur" onClick={() => sifreSifirla(p)}>
                                    <KeyRound size={15} />
                                  </button>
                                  <button
                                    type="button"
                                    className={`pyn-icon ${p.isActive ? "danger" : "ok"}`}
                                    title={p.isActive ? "Pasife al" : "Aktif et"}
                                    aria-label={p.isActive ? "Pasife al" : "Aktif et"}
                                    onClick={() => durumDegistir(p)}
                                  >
                                    {p.isActive ? <UserX size={15} /> : <UserCheck size={15} />}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!liste.personel.length && <div className="pyn-empty">{yukleniyor ? "Yükleniyor…" : "Bu filtrelerle personel bulunamadı."}</div>}
            </div>
            {liste.pagination?.totalPages > 1 && (
              <div className="pyn-pagination">
                <span>
                  Sayfa {liste.pagination.page} / {liste.pagination.totalPages} · {liste.pagination.total} kişi
                </span>
                <div>
                  <button type="button" disabled={filtre.page <= 1} onClick={() => setFiltre({ ...filtre, page: filtre.page - 1 })}>
                    Önceki
                  </button>
                  <button type="button" disabled={filtre.page >= liste.pagination.totalPages} onClick={() => setFiltre({ ...filtre, page: filtre.page + 1 })}>
                    Sonraki
                  </button>
                </div>
              </div>
            )}
          </section>
        ) : (
          <Departmanlar ctx={ctx} yonet={!!yetki.departmanYonet} onDegisti={contextYukle} bildir={setBildirim} />
        )}

        {form && <PersonelFormu form={form} setForm={setForm} ctx={ctx} departmanMap={departmanMap} onSubmit={kaydet} />}
        {sifre && <GeciciSifre bilgi={sifre} onClose={() => setSifre(null)} />}
      </div>
    </Layout>
  );
}

function PersonelFormu({ form, setForm, ctx, departmanMap, onSubmit }) {
  const ben = form.mod === "duzenle" && String(form.id) === String(ctx?.benId);
  const departman = departmanMap.get(String(form.departmanId));
  const altlar = (departman?.subDepartman || []).filter((s) => s.isActive || String(s._id) === String(form.subDepartmanId));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="pyn-modal" role="dialog" aria-modal="true" aria-labelledby="pyn-form-baslik">
      <form className="pyn-modal-card" onSubmit={onSubmit}>
        <header>
          <h2 id="pyn-form-baslik">{form.mod === "ekle" ? "Personel ekle" : "Personeli düzenle"}</h2>
          <button type="button" className="pyn-icon" aria-label="Kapat" onClick={() => setForm(null)}>
            <X size={16} />
          </button>
        </header>
        <div className="pyn-form-grid">
          <label>
            Ad
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
          </label>
          <label>
            Soyad
            <input required value={form.surname} onChange={(e) => set("surname", e.target.value)} />
          </label>
          <label>
            E-posta
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="off" />
          </label>
          <label>
            Telefon
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xx xxx xx xx" />
          </label>
          <label>
            Departman
            <select required value={form.departmanId} onChange={(e) => setForm((f) => ({ ...f, departmanId: e.target.value, subDepartmanId: "" }))}>
              <option value="">Seçin</option>
              {(ctx?.departmanlar || [])
                .filter((d) => d.isActive || String(d._id) === String(form.departmanId))
                .map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Alt departman
            <select required value={form.subDepartmanId} onChange={(e) => set("subDepartmanId", e.target.value)} disabled={!departman}>
              <option value="">{departman ? (altlar.length ? "Seçin" : "Bu departmanda alt departman yok") : "Önce departman seçin"}</option>
              {altlar.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="span2">
            Rol
            <select required value={form.roleId} onChange={(e) => set("roleId", e.target.value)} disabled={ben}>
              <option value="">Seçin</option>
              {(ctx?.roller || []).map((r) => (
                <option key={r._id} value={r._id} disabled={!r.atanabilir && String(r._id) !== String(form.roleId)}>
                  {r.name} ({r.yetkiSayisi} yetki){r.atanabilir ? "" : " · yetkiniz yetmiyor"}
                </option>
              ))}
            </select>
            <small>{ben ? "Kendi rolünüzü buradan değiştiremezsiniz." : "Yalnız sizin yetkilerinizin kapsadığı roller atanabilir. Rollerin yetkileri Rol Yönetimi sayfasındadır."}</small>
          </label>
          <label className="pyn-check span2">
            <input type="checkbox" checked={form.otpStatus} onChange={(e) => set("otpStatus", e.target.checked)} />
            <span>Girişte doğrulama kodu istensin (önerilir)</span>
          </label>
          {form.mod === "ekle" && (
            <label className="span2">
              İlk şifre
              <input type="password" value={form.sifre} onChange={(e) => set("sifre", e.target.value)} minLength={8} autoComplete="new-password" placeholder="Boş bırakın: güçlü bir geçici şifre üretilir" />
              <small>Boş bırakırsanız 14 karakterlik geçici şifre üretilir ve yalnız bir kez gösterilir.</small>
            </label>
          )}
        </div>
        <footer>
          <button type="button" className="pyn-btn" onClick={() => setForm(null)}>
            Vazgeç
          </button>
          <button type="submit" className="pyn-btn primary">
            {form.mod === "ekle" ? (
              <>
                <UserPlus size={16} /> Personeli ekle
              </>
            ) : (
              "Değişiklikleri kaydet"
            )}
          </button>
        </footer>
      </form>
    </div>
  );
}

function GeciciSifre({ bilgi, onClose }) {
  const [kopyalandi, setKopyalandi] = useState(false);
  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(bilgi.sifre);
      setKopyalandi(true);
    } catch {
      window.prompt("Şifreyi kopyalayın:", bilgi.sifre);
    }
  };
  return (
    <div className="pyn-modal" role="dialog" aria-modal="true" aria-labelledby="pyn-sifre-baslik">
      <div className="pyn-modal-card narrow">
        <header>
          <h2 id="pyn-sifre-baslik">Geçici şifre</h2>
        </header>
        <p>
          <b>{bilgi.kisi}</b> ({bilgi.email}) bu şifreyle giriş yapabilir. Şifre bir daha gösterilmez; kişiye güvenli bir yoldan iletin ve ilk girişte profilinden değiştirmesini isteyin.
        </p>
        <div className="pyn-sifre">
          <code>{bilgi.sifre}</code>
          <button type="button" className="pyn-btn primary" onClick={kopyala}>
            <Copy size={15} /> {kopyalandi ? "Kopyalandı" : "Kopyala"}
          </button>
        </div>
        <footer>
          <button type="button" className="pyn-btn" onClick={onClose}>
            Kapat
          </button>
        </footer>
      </div>
    </div>
  );
}

function Departmanlar({ ctx, yonet, onDegisti, bildir }) {
  const [duzen, setDuzen] = useState(null); // { id?, name, altlar: [{_id?, name, isActive}], isActive }

  const kaydet = async (e) => {
    e.preventDefault();
    try {
      const altlar = duzen.altlar.map((a) => ({ ...a, name: a.name.trim() })).filter((a) => a.name);
      if (duzen.id) await guncelleDepartman({ id: duzen.id, name: duzen.name, isActive: duzen.isActive, altlar });
      else await ekleDepartman({ name: duzen.name, altlar: altlar.map((a) => a.name) });
      bildir({ tip: "ok", mesaj: duzen.id ? "Departman güncellendi." : "Departman eklendi." });
      setDuzen(null);
      onDegisti();
    } catch (err) {
      bildir({ tip: "hata", mesaj: apiHata(err) });
    }
  };

  const altSet = (i, k, v) => setDuzen((d) => ({ ...d, altlar: d.altlar.map((a, j) => (j === i ? { ...a, [k]: v } : a)) }));

  return (
    <section className="pyn-panel">
      <div className="pyn-toolbar">
        <p className="pyn-muted">Personel bir departmana ve onun alt departmanına bağlanır. Kullanılan alt departmanlar silinmez, pasife alınır.</p>
        {yonet && (
          <button type="button" className="pyn-btn primary" onClick={() => setDuzen({ name: "", altlar: [{ name: "", isActive: true }], isActive: true })}>
            <Plus size={16} /> Departman ekle
          </button>
        )}
      </div>
      <div className="pyn-depts">
        {(ctx?.departmanlar || []).map((d) => (
          <article key={d._id} className={`pyn-dept${d.isActive ? "" : " pasif"}`}>
            <header>
              <h3>
                {d.name}
                {!d.isActive && <span className="pyn-chip off">pasif</span>}
              </h3>
              {yonet && (
                <button
                  type="button"
                  className="pyn-icon"
                  aria-label={`${d.name} düzenle`}
                  onClick={() => setDuzen({ id: d._id, name: d.name, isActive: d.isActive, altlar: d.subDepartman.filter((s) => s.isActive).map((s) => ({ ...s })) })}
                >
                  <Pencil size={15} />
                </button>
              )}
            </header>
            <div className="pyn-subs">
              {d.subDepartman.filter((s) => s.isActive).map((s) => (
                <span key={s._id} className="pyn-chip">
                  {s.name}
                </span>
              ))}
              {!d.subDepartman.some((s) => s.isActive) && <span className="pyn-muted">Alt departman yok</span>}
            </div>
          </article>
        ))}
      </div>

      {duzen && (
        <div className="pyn-modal" role="dialog" aria-modal="true" aria-labelledby="pyn-dept-baslik">
          <form className="pyn-modal-card narrow" onSubmit={kaydet}>
            <header>
              <h2 id="pyn-dept-baslik">{duzen.id ? "Departmanı düzenle" : "Departman ekle"}</h2>
              <button type="button" className="pyn-icon" aria-label="Kapat" onClick={() => setDuzen(null)}>
                <X size={16} />
              </button>
            </header>
            <label>
              Departman adı
              <input required value={duzen.name} onChange={(e) => setDuzen({ ...duzen, name: e.target.value })} autoFocus />
            </label>
            <div className="pyn-alt-list">
              <span>Alt departmanlar</span>
              {duzen.altlar.map((a, i) => (
                <div key={a._id || `yeni-${i}`} className="pyn-alt-row">
                  <input value={a.name} onChange={(e) => altSet(i, "name", e.target.value)} aria-label={`Alt departman ${i + 1}`} placeholder="Alt departman adı" />
                  <button
                    type="button"
                    className="pyn-icon danger"
                    aria-label="Alt departmanı kaldır"
                    onClick={() => setDuzen((d) => ({ ...d, altlar: d.altlar.filter((_, j) => j !== i) }))}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button type="button" className="pyn-btn" onClick={() => setDuzen((d) => ({ ...d, altlar: [...d.altlar, { name: "", isActive: true }] }))}>
                <Plus size={15} /> Alt departman ekle
              </button>
            </div>
            {duzen.id && (
              <label className="pyn-check">
                <input type="checkbox" checked={duzen.isActive} onChange={(e) => setDuzen({ ...duzen, isActive: e.target.checked })} />
                <span>Departman aktif</span>
              </label>
            )}
            <footer>
              <button type="button" className="pyn-btn" onClick={() => setDuzen(null)}>
                Vazgeç
              </button>
              <button type="submit" className="pyn-btn primary">
                Kaydet
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
